import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Avatar,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  MoreVert,
  Check,
  Close,
  VisibilityOff,
  Block,
  Refresh,
  Warning,
  Flag,
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { useReports, useBlockedUsers } from '../../hooks/useCommunity';
import {
  useUpdateReport,
  useHidePost,
  useHideComment,
  useBlockUser,
  useUnblockUser,
} from '../../hooks/useCommunityMutations';
import type { CommunityReport, ReportFilters } from '../../types/community';
import { COLORS } from '@elevair/shared';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function AdminModerationPanel() {
  const [tabValue, setTabValue] = useState(0);
  const [reportFilters, setReportFilters] = useState<ReportFilters>({ status: 'pending' });
  const [selectedReport, setSelectedReport] = useState<CommunityReport | null>(null);
  const [reportMenuAnchor, setReportMenuAnchor] = useState<null | HTMLElement>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockUserId, setBlockUserId] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockDuration, setBlockDuration] = useState<number | undefined>(undefined);

  // Queries
  const { data: reportsData, isLoading: loadingReports, refetch: refetchReports } = useReports(reportFilters);
  const { data: blocksData, isLoading: loadingBlocks, refetch: refetchBlocks } = useBlockedUsers();

  // Mutations
  const updateReport = useUpdateReport();
  const hidePost = useHidePost();
  const hideComment = useHideComment();
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();

  const reports = reportsData?.data?.reports || [];
  const blocks = blocksData?.data?.blocks || [];

  const handleReportAction = async (action: 'reviewed' | 'resolved' | 'dismissed') => {
    if (!selectedReport) return;

    try {
      await updateReport.mutateAsync({
        id: selectedReport._id,
        data: { status: action },
      });
      setReportMenuAnchor(null);
      setSelectedReport(null);
    } catch (error) {
      console.error('Failed to update report:', error);
    }
  };

  const handleHideContent = async () => {
    if (!selectedReport) return;

    try {
      if (selectedReport.targetType === 'post') {
        await hidePost.mutateAsync({
          id: selectedReport.targetId,
          reason: 'Reported content removed by moderator',
        });
      } else {
        await hideComment.mutateAsync({
          id: selectedReport.targetId,
          reason: 'Reported content removed by moderator',
        });
      }
      // Also mark report as resolved
      await updateReport.mutateAsync({
        id: selectedReport._id,
        data: { status: 'resolved' },
      });
      setReportMenuAnchor(null);
      setSelectedReport(null);
    } catch (error) {
      console.error('Failed to hide content:', error);
    }
  };

  const handleBlockUser = async () => {
    if (!blockUserId) return;

    try {
      await blockUser.mutateAsync({
        userId: blockUserId,
        data: {
          reason: blockReason || 'Violated community guidelines',
          duration: blockDuration,
        },
      });
      setBlockDialogOpen(false);
      setBlockUserId(null);
      setBlockReason('');
      setBlockDuration(undefined);
      refetchBlocks();
    } catch (error) {
      console.error('Failed to block user:', error);
    }
  };

  const handleUnblockUser = async (userId: string) => {
    try {
      await unblockUser.mutateAsync(userId);
      refetchBlocks();
    } catch (error) {
      console.error('Failed to unblock user:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'reviewed':
        return 'info';
      case 'resolved':
        return 'success';
      case 'dismissed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getAvatarColor = (id: string) => {
    const colors = [COLORS.primary, COLORS.secondary, COLORS.accent, '#9C27B0', '#FF5722'];
    const index = id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#DC2626',
              }}
            >
              <Warning />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Moderation Panel
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Flag fontSize="small" />
                Reports
                {reports.length > 0 && (
                  <Chip label={reports.length} size="small" color="warning" sx={{ height: 20 }} />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Block fontSize="small" />
                Blocked Users
              </Box>
            }
          />
        </Tabs>

        {/* Reports Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button
              size="small"
              variant={reportFilters.status === 'pending' ? 'contained' : 'outlined'}
              onClick={() => setReportFilters({ status: 'pending' })}
            >
              Pending
            </Button>
            <Button
              size="small"
              variant={reportFilters.status === 'reviewed' ? 'contained' : 'outlined'}
              onClick={() => setReportFilters({ status: 'reviewed' })}
            >
              Reviewed
            </Button>
            <Button
              size="small"
              variant={reportFilters.status === 'resolved' ? 'contained' : 'outlined'}
              onClick={() => setReportFilters({ status: 'resolved' })}
            >
              Resolved
            </Button>
            <Button
              size="small"
              variant={!reportFilters.status ? 'contained' : 'outlined'}
              onClick={() => setReportFilters({})}
            >
              All
            </Button>
            <Box flex={1} />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => refetchReports()}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {loadingReports ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : reports.length === 0 ? (
            <Alert severity="info">No reports found</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reporter</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: '0.75rem',
                              bgcolor: getAvatarColor(report.reportedBy._id),
                            }}
                          >
                            {report.reportedBy.firstName?.charAt(0)}
                            {report.reportedBy.lastName?.charAt(0)}
                          </Avatar>
                          <Typography variant="body2">
                            {report.reportedBy.firstName} {report.reportedBy.lastName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={report.targetType}
                          size="small"
                          sx={{ textTransform: 'capitalize', height: 24 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={report.details || ''}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {report.reason.replace(/_/g, ' ')}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={report.status}
                          size="small"
                          color={getStatusColor(report.status)}
                          sx={{ textTransform: 'capitalize', height: 24 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setSelectedReport(report);
                            setReportMenuAnchor(e.currentTarget);
                          }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Blocked Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => refetchBlocks()}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {loadingBlocks ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : blocks.length === 0 ? (
            <Alert severity="info">No blocked users</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Blocked By</TableCell>
                    <TableCell>Blocked On</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blocks.map((block) => (
                    <TableRow key={block._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: '0.75rem',
                              bgcolor: getAvatarColor(block.blockedUser._id),
                            }}
                          >
                            {block.blockedUser.firstName?.charAt(0)}
                            {block.blockedUser.lastName?.charAt(0)}
                          </Avatar>
                          <Typography variant="body2">
                            {block.blockedUser.firstName} {block.blockedUser.lastName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{block.reason}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {block.blockedBy.firstName} {block.blockedBy.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {format(new Date(block.createdAt), 'MMM d, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {block.expiresAt ? (
                          <Typography variant="caption">
                            {format(new Date(block.expiresAt), 'MMM d, yyyy')}
                          </Typography>
                        ) : (
                          <Chip label="Permanent" size="small" color="error" sx={{ height: 24 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="success"
                          onClick={() => handleUnblockUser(block.blockedUser._id)}
                          disabled={unblockUser.isPending}
                        >
                          Unblock
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </CardContent>

      {/* Report Actions Menu */}
      <Menu
        anchorEl={reportMenuAnchor}
        open={Boolean(reportMenuAnchor)}
        onClose={() => {
          setReportMenuAnchor(null);
          setSelectedReport(null);
        }}
      >
        {selectedReport?.status === 'pending' && (
          <MenuItem onClick={() => handleReportAction('reviewed')}>
            <Check fontSize="small" sx={{ mr: 1 }} />
            Mark as Reviewed
          </MenuItem>
        )}
        <MenuItem onClick={() => handleReportAction('resolved')}>
          <Check fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
          Mark as Resolved
        </MenuItem>
        <MenuItem onClick={() => handleReportAction('dismissed')}>
          <Close fontSize="small" sx={{ mr: 1 }} />
          Dismiss Report
        </MenuItem>
        <MenuItem onClick={handleHideContent} sx={{ color: 'warning.main' }}>
          <VisibilityOff fontSize="small" sx={{ mr: 1 }} />
          Hide Content
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedReport?.targetAuthor) {
              setBlockUserId(selectedReport.targetAuthor._id);
              setBlockDialogOpen(true);
              setReportMenuAnchor(null);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <Block fontSize="small" sx={{ mr: 1 }} />
          Block User
        </MenuItem>
      </Menu>

      {/* Block User Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#DC2626',
              }}
            >
              <Block />
            </Box>
            <Typography variant="h6">Block User</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This will prevent the user from accessing the Community Hub.
          </Typography>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={2}
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="Reason for blocking (visible to admins)"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Duration (days)"
            type="number"
            fullWidth
            value={blockDuration || ''}
            onChange={(e) => setBlockDuration(e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="Leave empty for permanent block"
            helperText="Leave empty for permanent block"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBlockUser}
            disabled={blockUser.isPending}
          >
            {blockUser.isPending ? 'Blocking...' : 'Block User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
